import React, { useState, useEffect, useCallback } from 'react';
import { Tabs, Empty, Modal, Input, Spin, Table, Button, message } from 'antd';
import { EyeOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import moment from 'moment';
import { Box, Text } from 'atoms';
import VendorBankDetailsCardV2 from './VendorBankDetailsCardV2';
import {
	searchPendingApprovals,
	approveVendorBankDetails,
	rejectVendorBankDetails,
} from 'store/approvalSliceV2';

const PageContainer = styled(Box)`
	max-width: 1600px;
	margin: 0 auto;
	padding: 24px;
`;

const HeaderContainer = styled(Box)`
	margin-bottom: 24px;
`;

const PageTitle = styled(Text)`
	font-size: 24px;
	font-weight: 600;
	color: #333;
	display: block;
	margin-bottom: 4px;
`;

const PageSubtitle = styled(Text)`
	font-size: 14px;
	color: #707070;
	display: block;
`;

const TabsContainer = styled(Box)`
	margin-top: 24px;
`;

const StyledTabs = styled(Tabs)`
	.ant-tabs-tab {
		font-size: 16px;
		font-weight: 500;
		padding: 12px 24px;
	}

	.ant-tabs-tab-active {
		.ant-tabs-tab-btn {
			color: #1890ff;
			font-weight: 600;
		}
	}

	.ant-tabs-ink-bar {
		background: #1890ff;
	}
`;

const ContentContainer = styled(Box)`
	margin-top: 24px;
`;

const EmptyStateContainer = styled(Box)`
	padding: 60px 20px;
	text-align: center;
`;

const LoadingContainer = styled(Box)`
	display: flex;
	justify-content: center;
	align-items: center;
	padding: 60px 20px;
`;

const StyledTable = styled(Table)`
	.ant-table-thead > tr > th {
		background-color: #18405d;
		color: #fff;
		font-weight: 600;
	}

	.ant-table-tbody > tr:nth-child(even) {
		background-color: #f5f5f5;
	}

	.ant-table-tbody > tr:hover {
		background-color: #e6f7ff;
	}
`;

const ApproveModalContent = styled(Box)`
	text-align: center;
`;

const ModalTitle = styled(Text)`
	font-size: 20px;
	font-weight: 600;
	color: #000;
	display: block;
	margin-bottom: 16px;
`;

const ModalMessage = styled(Text)`
	font-size: 14px;
	color: #666;
	display: block;
	margin-bottom: 24px;
	line-height: 1.5;
`;

const ModalButtonGroup = styled(Box)`
	display: flex;
	justify-content: center;
	gap: 12px;
	margin-top: 24px;
`;

const CancelButton = styled(Button)`
	border-color: #1890ff;
	color: #1890ff;
	background: #fff;
	min-width: 100px;

	&:hover {
		border-color: #40a9ff;
		color: #40a9ff;
		background: #fff;
	}
`;

const ApproveButtonStyled = styled(Button)`
	background: #1890ff;
	border-color: #1890ff;
	color: #fff;
	min-width: 100px;

	&:hover {
		background: #40a9ff;
		border-color: #40a9ff;
	}
`;

const { TextArea } = Input;

const maskAccountNumber = (accountNumber) => {
	if (!accountNumber) return '-';
	if (accountNumber.length <= 8) return accountNumber;
	return `${accountNumber.substring(0, 3)}${'*'.repeat(
		accountNumber.length - 6
	)}${accountNumber.substring(accountNumber.length - 3)}`;
};

export default function VendorBankDetailsApprovalPageV2() {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const [activeTab, setActiveTab] = useState('pending');
	const [approveModalVisible, setApproveModalVisible] = useState(false);
	const [rejectModalVisible, setRejectModalVisible] = useState(false);
	const [selectedApproval, setSelectedApproval] = useState(null);
	const [comments, setComments] = useState('');
	const [rejectionReason, setRejectionReason] = useState('');

	const { approvals, loading, actionLoading } = useSelector((state) => state.approvalStateV2);

	const fetchApprovals = useCallback(
		(status) => {
			dispatch(
				searchPendingApprovals({
					filter_by: {
						approval_type: 'vendor_bank_details',
						status: status === 'pending' ? 'pending' : status,
					},
					pagination: { page: 1, per_page: 20 },
				})
			);
		},
		[dispatch]
	);

	useEffect(() => {
		fetchApprovals(activeTab);
	}, [activeTab, fetchApprovals]);

	const handleTabChange = (key) => {
		setActiveTab(key);
	};

	const handleView = (id, approvalId) => {
		// Navigate to approval details page with approval ID
		if (approvalId) {
			navigate(`/approvals/vendor-bank-details/${approvalId}`, {
				state: { fromApproval: true, approvalId, entityId: id },
			});
		} else {
			// Fallback to vendor bank details route if no approval ID
			navigate(`/vendor-bank-details/${id}`, {
				state: { fromApproval: true, approvalId },
			});
		}
	};

	const handleApproveClick = (approval) => {
		setSelectedApproval(approval);
		setComments('');
		setApproveModalVisible(true);
	};

	const handleRejectClick = (approval) => {
		setSelectedApproval(approval);
		setRejectionReason('');
		setRejectModalVisible(true);
	};

	const handleApproveConfirm = async () => {
		if (!selectedApproval) return;

		if (!comments.trim()) {
			message.error('Comments are required for approval');
			return;
		}

		// Use approval_id from the approval object
		const approvalId = selectedApproval.id;
		const result = await dispatch(
			approveVendorBankDetails({
				approval_id: approvalId,
				comments,
			})
		);
		if (result.success) {
			setApproveModalVisible(false);
			setSelectedApproval(null);
			setComments('');
			// Refresh the list
			fetchApprovals(activeTab);
		}
	};

	const handleRejectConfirm = async () => {
		if (!selectedApproval || !rejectionReason.trim()) {
			return;
		}

		// Use approval_id from the approval object
		const approvalId = selectedApproval.id;
		const result = await dispatch(
			rejectVendorBankDetails({
				approval_id: approvalId,
				rejection_reason: rejectionReason,
			})
		);
		if (result.success) {
			setRejectModalVisible(false);
			setSelectedApproval(null);
			setRejectionReason('');
			// Refresh the list
			fetchApprovals(activeTab);
		}
	};

	const formatDate = (dateString) => {
		if (!dateString) return '-';
		return moment(dateString).format('DD-MM-YYYY');
	};

	const formatTime = (dateString) => {
		if (!dateString) return '-';
		return moment(dateString).format('HH:mm');
	};

	const getApprovedColumns = () => [
		{
			title: 'Approved at',
			key: 'approved_at',
			width: 180,
			render: (_, record) =>
				!record.approved_at ? (
					'-'
				) : (
					<>
						<div>{formatDate(record.approved_at)}</div>
						<div style={{ fontSize: '12px', color: '#666' }}>
							{formatTime(record.approved_at)}
						</div>
					</>
				),
		},
		{
			title: 'Approved by',
			dataIndex: 'approved_by',
			key: 'approved_by',
			width: 150,
		},
	];

	const getRejectedColumns = () => [
		{
			title: 'Rejected at',
			key: 'rejected_at',
			width: 180,
			render: (_, record) =>
				!record.rejected_at && !record.approved_at ? (
					'-'
				) : (
					<>
						<div>{formatDate(record.rejected_at || record.approved_at)}</div>
						<div style={{ fontSize: '12px', color: '#666' }}>
							{formatTime(record.rejected_at || record.approved_at)}
						</div>
					</>
				),
		},
		{
			title: 'Rejected by',
			dataIndex: 'rejected_by',
			key: 'rejected_by',
			width: 150,
			render: (_, record) => record.rejected_by || record.approved_by || '-',
		},
	];

	const getActionColumn = () => ({
		title: 'Action',
		key: 'action',
		width: 100,
		fixed: 'right',
		render: (_, record) => (
			<Button
				type="text"
				icon={<EyeOutlined />}
				onClick={() => handleView(record.entity_id || record.id, record.approval_id)}
				title="View Details"
			/>
		),
	});

	const getTableColumns = (tabKey) => {
		const baseColumns = [
			{
				title: 'Vendor Name',
				dataIndex: 'vendor_name',
				key: 'vendor_name',
				width: 180,
			},
			{
				title: 'Bank Account Number',
				key: 'account_number',
				width: 180,
				render: (_, record) => maskAccountNumber(record.account_number),
			},
			{
				title: 'IFSC Code',
				dataIndex: 'ifsc_code',
				key: 'ifsc_code',
				width: 150,
			},
			{
				title: 'Bank Name',
				dataIndex: 'bank_name',
				key: 'bank_name',
				width: 180,
			},
			{
				title: 'Bank Branch',
				dataIndex: 'branch_name',
				key: 'branch_name',
				width: 180,
			},
			// Only show Document Attached column for pending tab
			...(tabKey === 'pending'
				? [
						{
							title: 'Document Attached',
							key: 'document',
							width: 200,
							render: (_, record) =>
								record.document_url || record.document_name ? (
									<a
										href={record.document_url}
										target="_blank"
										rel="noopener noreferrer"
										style={{ color: '#1890ff' }}
									>
										{record.document_name || 'View Document'}
									</a>
								) : (
									'-'
								),
						},
				  ]
				: []),
			{
				title: 'Created by',
				dataIndex: 'created_by',
				key: 'created_by',
				width: 150,
			},
		];

		// Add approval columns for approved/rejected tabs
		if (tabKey === 'approved') {
			baseColumns.push(...getApprovedColumns());
		} else if (tabKey === 'rejected') {
			baseColumns.push(...getRejectedColumns());
		} else {
			// Add view action column only for pending tab
			baseColumns.push(getActionColumn());
		}

		return baseColumns;
	};

	// Helper functions to extract values from approval object
	const getVendorName = (approval, entityDetails) =>
		entityDetails?.vendor_name ||
		approval.entity_summary?.vendor_name ||
		approval.entity_data?.vendor_name ||
		approval.related_data?.vendor?.name ||
		'-';

	const getBankField = (approval, field) =>
		approval.entity_data?.[field] || approval.entity_data?.banking_info?.[field] || '-';

	const getDocumentUrl = (approval) =>
		approval.entity_data?.document_url ||
		approval.entity_data?.banking_info?.document_url ||
		approval.entity_data?.document_info?.url ||
		null;

	const getDocumentName = (approval) =>
		approval.entity_data?.document_name ||
		approval.entity_data?.banking_info?.document_name ||
		approval.entity_data?.document_info?.name ||
		null;

	const getApprovedBy = (approval) =>
		approval.approved_by_name ||
		approval.approver_name ||
		approval.related_data?.approver?.name ||
		(approval.approver_id ? `User ${approval.approver_id}` : '-');

	const getRejectedBy = (approval) =>
		approval.rejected_by_name ||
		approval.related_data?.rejecter?.name ||
		(approval.rejecter_id ? `User ${approval.rejecter_id}` : '-');

	const mapApprovalToTableData = (approval) => ({
		key: approval.id,
		id: approval.entity_id || approval.id,
		entity_id: approval.entity_id || approval.id,
		approval_id: approval.id,
		vendor_name: getVendorName(approval, approval.entity_details),
		account_number: getBankField(approval, 'account_number'),
		ifsc_code: getBankField(approval, 'ifsc_code'),
		bank_name: getBankField(approval, 'bank_name'),
		branch_name: getBankField(approval, 'branch_name'),
		document_url: getDocumentUrl(approval),
		document_name: getDocumentName(approval),
		created_by: approval.requested_by_name || '-',
		approved_at: approval.approved_at || null,
		approved_by: getApprovedBy(approval),
		rejected_at: approval.rejected_at || null,
		rejected_by: getRejectedBy(approval),
	});

	const renderTableContent = (tabKey) => {
		const tableData = approvals.map(mapApprovalToTableData);
		const columns = getTableColumns(tabKey);

		return (
			<ContentContainer>
				<StyledTable
					columns={columns}
					dataSource={tableData}
					pagination={{
						pageSize: 10,
						showSizeChanger: true,
						showTotal: (total) => `Total ${total} items`,
						position: ['bottomRight'],
					}}
					scroll={{ x: 'max-content' }}
					locale={{
						emptyText: (
							<Empty
								description={
									<Text fontSize="14px" color="#999">
										No {tabKey} vendor bank details found
									</Text>
								}
							/>
						),
					}}
				/>
			</ContentContainer>
		);
	};

	/**
	 * Extract filename from document_key (remove UUID prefix)
	 */
	const extractFilenameFromS3Key = (s3Key) => {
		if (!s3Key) return '';
		const uuidPattern = /^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}-/i;
		const match = s3Key.match(uuidPattern);
		if (match) {
			return s3Key.substring(match[0].length);
		}
		return s3Key;
	};

	/**
	 * Extract document_key from approval entity_data
	 */
	const getDocumentKey = (approval) =>
		approval.entity_data?.banking_info?.document_info?.branch_document?.document_key || null;

	/**
	 * Map approval data to bank details format
	 */
	const mapApprovalToBankDetails = (approval) => {
		const documentKey = getDocumentKey(approval);
		const documentName = documentKey ? extractFilenameFromS3Key(documentKey) : null;

		return {
			id: approval.entity_id || approval.id,
			approval_id: approval.id,
			vendor_name:
				approval.entity_details?.vendor_name || approval.entity_summary?.vendor_name || '-',
			account_number:
				approval.entity_data?.banking_info?.account_number ||
				approval.entity_summary?.account_number ||
				'-',
			ifsc_code:
				approval.entity_data?.banking_info?.ifsc_code ||
				approval.entity_summary?.ifsc_code ||
				'-',
			bank_name:
				approval.entity_data?.banking_info?.bank_name ||
				approval.entity_summary?.bank_name ||
				'-',
			branch_name: approval.entity_data?.banking_info?.branch_name || '-',
			document_key: documentKey,
			document_name: documentName,
			created_by: approval.entity_summary?.created_by || approval.requested_by_name || '-',
		};
	};

	const renderContent = (tabKey) => {
		if (loading) {
			return (
				<LoadingContainer>
					<Spin size="large" tip="Loading approvals..." />
				</LoadingContainer>
			);
		}

		if (!approvals || approvals.length === 0) {
			return (
				<EmptyStateContainer>
					<Empty
						description={
							<Text fontSize="14px" color="#999">
								No {tabKey} vendor bank details found
							</Text>
						}
					/>
				</EmptyStateContainer>
			);
		}

		// Show table for approved/rejected tabs, cards for pending
		if (tabKey === 'approved' || tabKey === 'rejected') {
			return renderTableContent(tabKey);
		}

		// Show cards for pending tab
		return (
			<ContentContainer>
				{approvals.map((approval) => {
					const bankDetails = mapApprovalToBankDetails(approval);

					return (
						<VendorBankDetailsCardV2
							key={approval.id}
							bankDetails={bankDetails}
							onView={() =>
								handleView(approval.entity_id || approval.id, approval.id)
							}
							onApprove={() => handleApproveClick(approval)}
							onReject={() => handleRejectClick(approval)}
						/>
					);
				})}
			</ContentContainer>
		);
	};

	const tabItems = [
		{
			key: 'pending',
			label: 'Pending',
			children: renderContent('pending'),
		},
		{
			key: 'approved',
			label: 'Approved',
			children: renderContent('approved'),
		},
		{
			key: 'rejected',
			label: 'Rejected',
			children: renderContent('rejected'),
		},
	];

	return (
		<PageContainer>
			<HeaderContainer>
				<PageTitle>Vendor Bank Details request approvals</PageTitle>
				<PageSubtitle>Approve vendor Bank Details requests</PageSubtitle>
			</HeaderContainer>

			<TabsContainer>
				<StyledTabs activeKey={activeTab} onChange={handleTabChange} items={tabItems} />
			</TabsContainer>

			{/* Approve Modal */}
			<Modal
				open={approveModalVisible}
				onCancel={() => {
					setApproveModalVisible(false);
					setSelectedApproval(null);
					setComments('');
				}}
				footer={null}
				width={500}
				centered
			>
				<ApproveModalContent>
					<ModalTitle>Approve Vendor Bank Details</ModalTitle>
					<ModalMessage>
						This bank details will be approved and you won&apos;t be able to revert this
						change.
					</ModalMessage>
					<Box textAlign="left" mb={3}>
						<Text
							fontSize="14px"
							color="#333"
							mb={1}
							display="block"
							style={{ textAlign: 'left' }}
						>
							Comments <span style={{ color: '#ff4d4f' }}>*</span>
						</Text>
						<TextArea
							rows={4}
							value={comments}
							onChange={(e) => setComments(e.target.value)}
							placeholder="Enter comments for approval"
							maxLength={500}
							showCount
						/>
					</Box>
					<ModalButtonGroup>
						<CancelButton
							onClick={() => {
								setApproveModalVisible(false);
								setSelectedApproval(null);
								setComments('');
							}}
						>
							Cancel
						</CancelButton>
						<ApproveButtonStyled
							onClick={handleApproveConfirm}
							loading={actionLoading}
							disabled={!comments.trim()}
						>
							Approve
						</ApproveButtonStyled>
					</ModalButtonGroup>
				</ApproveModalContent>
			</Modal>

			{/* Reject Modal */}
			<Modal
				open={rejectModalVisible}
				onCancel={() => {
					setRejectModalVisible(false);
					setSelectedApproval(null);
					setRejectionReason('');
				}}
				footer={null}
				width={500}
				centered
			>
				<ApproveModalContent>
					<ModalTitle>Reject Vendor Bank Details</ModalTitle>
					<ModalMessage>
						This bank details will be rejected and you won&apos;t be able to revert this
						change.
					</ModalMessage>
					<Box textAlign="left" mb={3}>
						<Text
							fontSize="14px"
							color="#333"
							mb={1}
							display="block"
							style={{ textAlign: 'left' }}
						>
							Rejection Reason <span style={{ color: '#ff4d4f' }}>*</span>
						</Text>
						<TextArea
							rows={4}
							value={rejectionReason}
							onChange={(e) => setRejectionReason(e.target.value)}
							placeholder="Enter rejection reason"
							maxLength={500}
							showCount
						/>
					</Box>
					<ModalButtonGroup>
						<CancelButton
							onClick={() => {
								setRejectModalVisible(false);
								setSelectedApproval(null);
								setRejectionReason('');
							}}
						>
							Cancel
						</CancelButton>
						<ApproveButtonStyled
							onClick={handleRejectConfirm}
							loading={actionLoading}
							disabled={!rejectionReason.trim()}
							danger
						>
							Reject
						</ApproveButtonStyled>
					</ModalButtonGroup>
				</ApproveModalContent>
			</Modal>
		</PageContainer>
	);
}
